/**
 * AssignEmployeesDialog.tsx
 * Dialog for bulk-assigning employees to a ScheduleTask.
 *
 * Phase 3, Item 6 — Assign-from-Task flow.
 * Phase 3, Item 7 — Skill-gate: opens MentorRequiredModal on 409.
 * Wave 3 (percent retirement): the per-row Percent column, the total-% chip
 * and the tier defaults (Lead/Senior 25%) are GONE — an assignment is
 * presence (busy day); every row writes 100 (server pins it anyway). The
 * mentor added from the gate is a presence like everyone else. Assigning
 * onto a day off returns 409 absence_day_conflict → the server message shows
 * verbatim as a hard error (Q3B — no Save-anyway).
 *
 * Layout:
 *   - Header: Task name + date range.
 *   - Body: Autocomplete multi-select for employees (shows Tier chip).
 *   - Selected rows: one per employee with Tier chip + remove.
 *   - Footer: Save / Cancel buttons.
 */

import React, { useEffect, useState, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Autocomplete,
  TextField,
  Box,
  Typography,
  Chip,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  CircularProgress,
  Alert,
  Tooltip,
  IconButton,
} from '@mui/material';
import { Close, PersonAdd } from '@mui/icons-material';
import { useTheme, alpha } from '@mui/material/styles';
import {
  listAssignableUsers,
  bulkAssignFromTask,
  weekOverloadHint,
  SkillGateError,
  type AssignableUser,
  type SkillGateBlockedUser,
  type EligibleMentor,
} from '../../../services/scheduleAssignments';
import { MentorRequiredModal } from '../../../components/schedule/MentorRequiredModal';
import { AssigneeOptionRow, TierChip, useAssigneeCapacity } from '../../../components/schedule/assigneeOption';
import {
  isQualifiedForTaskType,
  listHasQualifiedCandidate,
} from '../../../utils/scheduleQualification';
import { rankAssignees, isPickBlockedByAbsence } from '../../../utils/assigneeRanking';
import type { TaskWithBookings, TaskAssignee } from '../../../services/scheduleTasks';
import { listTaskAssignees, unassignUserFromTask } from '../../../services/scheduleTasks';
import { ConfirmDialog } from '../../../components/shared/ConfirmDialog';
import { useRemovalGuard } from './useRemovalGuard';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface SelectedEntry {
  user: AssignableUser;
}

interface Props {
  open: boolean;
  task: TaskWithBookings | null;
  /**
   * Optional user to pre-select in the picker. Used by the inline assignee
   * dropdown (BUG-2026-0085) to hand off when SkillGate/Overbook conflicts
   * need the full dialog UI to resolve.
   */
  preselectUser?: AssignableUser | null;
  onClose: () => void;
  /**
   * Success callback. G2-7: `overloadHint` is the non-blocking weekly-overload
   * advisory copy (weekOverloadHint) or null — parents with a snackbar should
   * append it so the planner hears about the overload at assign time.
   */
  onSaved: (count: number, overloadHint?: string | null) => void;
  /**
   * BUG-2026-0093 — called after an existing assignee is removed from within
   * the dialog (the dialog stays open so the user can remove several people),
   * so the parent can refresh that task's chips in the background.
   */
  onAssigneesChanged?: () => void;
  /**
   * FEAT-2026-0007 6C.1 — scenario context.
   * When set, assignments created by this dialog are stamped with this ScenarioKey.
   * Omit for the BASELINE (TaskTablePage) flow.
   */
  scenarioKey?: string;
  /**
   * Per-machine staffing (079): when the dialog was opened from a machine's "+"
   * (or a SkillGate/Overbook escalation of one), bind the saved assignments to
   * that MachineBooking. Omit/null for a task-level assign.
   */
  machineBookingKey?: string | null;
}

// ---------------------------------------------------------------------------
// Format date helper
// ---------------------------------------------------------------------------

function fmtDate(iso: string | null): string {
  if (!iso) return '—';
  const [y, m, d] = iso.split('-');
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  return `${months[parseInt(m, 10) - 1]} ${parseInt(d, 10)}, ${y}`;
}

// ---------------------------------------------------------------------------
// Dialog component
// ---------------------------------------------------------------------------

export const AssignEmployeesDialog: React.FC<Props> = ({
  open,
  task,
  preselectUser,
  onClose,
  onSaved,
  onAssigneesChanged,
  scenarioKey,
  machineBookingKey,
}) => {
  const theme = useTheme();

  const [allUsers, setAllUsers] = useState<AssignableUser[]>([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [selected, setSelected] = useState<SelectedEntry[]>([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  /**
   * Existing assignees on this task, fetched on open. Shown under "Currently
   * assigned"; each chip is removable (BUG-2026-0093).
   */
  const [existingAssignees, setExistingAssignees] = useState<TaskAssignee[]>([]);
  /** BUG-2026-0093 — UserKey of the existing assignee currently being removed. */
  const [removingExistingKey, setRemovingExistingKey] = useState<string | null>(null);
  // Skill-gate #4 — warn before removing the last L3+ supervisor from a task.
  const { guardRemoval, removalConfirm, closeRemovalConfirm } = useRemovalGuard();

  // Skill-gate modal state (Phase 3 Item 7)
  const [skillGateOpen, setSkillGateOpen] = useState(false);
  const [skillGateBlocked, setSkillGateBlocked] = useState<SkillGateBlockedUser[]>([]);
  const [skillGateTaskTypeKey, setSkillGateTaskTypeKey] = useState<string | null>(null);

  // Load assignable users whenever the dialog opens or the task's task type
  // changes.
  //
  // BUG-2026-0094 — pass the task's TaskTypeKey so each returned user carries a
  // `SkillLevel` for THIS task type. That lets us (a) hide un-rated users from
  // the picker via the shared qualification gate and (b) show their skill level
  // beside the name. The first fix only patched the inline picker; this advanced
  // dialog kept calling listAssignableUsers() with no task type, so it listed
  // every active employee regardless of skill — the surface Sam still saw the
  // bug on. No skill is requested (and nobody is hidden) when the task has no
  // task type. Re-fetching on task-type change also keeps the list fresh after
  // a Skill Matrix edit (the dialog mounts a new fetch each time it re-opens).
  const taskTypeKey = task?.TaskTypeKey ?? null;

  // Availability for the dropdown — each candidate's existing load, shown the same
  // way as the inline picker (shared module). No weekStart is passed, so the hook
  // fetches EVERY week the booking spans and shows the max load (P-18);
  // availabilityUnknown → neutral rows, never a false green "free".
  // RM-5: continuous tasks judge the full-duration attendance windows (every
  // working day of [setup..teardown] — lab answer Q2, 2026-07-10).
  const { relevantWeek, capacity, availabilityUnknown } = useAssigneeCapacity({
    open,
    bookings: task?.Bookings,
    machineBookingKey,
    scenarioKey,
    runMode: task?.RunMode ?? null,
    isPrepStep: task?.IsPrepStep,
  });

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setLoadingUsers(true);
    setError(null);
    listAssignableUsers(taskTypeKey)
      .then((users) => {
        if (!cancelled) setAllUsers(users);
      })
      .catch((err) => {
        if (!cancelled) setError(err instanceof Error ? err.message : 'Failed to load users.');
      })
      .finally(() => {
        if (!cancelled) setLoadingUsers(false);
      });
    return () => { cancelled = true; };
  }, [open, taskTypeKey]);

  // Reset selections + load existing assignees when dialog re-opens for a new task
  useEffect(() => {
    if (!open || !task) {
      setExistingAssignees([]);
      return;
    }
    // BUG-2026-0085: when the inline picker escalates a SkillGate, pre-select
    // the user it tried so the user doesn't have to re-pick.
    if (preselectUser) {
      setSelected([{ user: preselectUser }]);
    } else {
      setSelected([]);
    }
    setError(null);
    let cancelled = false;
    // Scenario-scope the "Currently assigned" list so the dialog matches the
    // grid that opened it (Phase 2 passes the draft key; non-scenario callers
    // pass undefined → unfiltered).
    listTaskAssignees(task.ScheduleTaskKey, scenarioKey)
      .then((rows) => { if (!cancelled) setExistingAssignees(rows); })
      .catch(() => { if (!cancelled) setExistingAssignees([]); });
    return () => { cancelled = true; };
  }, [open, task?.ScheduleTaskKey, task, preselectUser, scenarioKey]);

  // Deduplicate existing assignees by UserKey (one user can have multiple per-week rows)
  const uniqueExisting = React.useMemo(() => {
    const seen = new Set<string>();
    const out: TaskAssignee[] = [];
    for (const a of existingAssignees) {
      if (seen.has(a.UserKey)) continue;
      seen.add(a.UserKey);
      out.push(a);
    }
    return out;
  }, [existingAssignees]);

  // Picker options: drop users already on the task, then apply the BUG-2026-0094
  // qualification gate (shared with the inline picker) so un-rated users never
  // show for a task that has a task type. Levels 1–4 stay (qualified, possibly
  // mentor-gated by the 409 skill gate at save time); no task type → show all.
  //
  // IMP2-2 (audit #2) — then RANK (shared rankAssignees, same as the inline
  // picker): absent last, then free-days desc, then skill desc, then name.
  // While availability loads, skill + name only. Ordering only (G-1).
  const pickableUsers = React.useMemo(
    () =>
      rankAssignees(
        allUsers.filter(
          (u) =>
            !uniqueExisting.some((a) => a.UserKey === u.AppUserKey) &&
            isQualifiedForTaskType(u, taskTypeKey),
        ),
        capacity,
        !availabilityUnknown,
      ),
    [allUsers, uniqueExisting, taskTypeKey, capacity, availabilityUnknown],
  );

  // Whether the loaded list had anyone qualified for this task type — drives a
  // clearer empty-state message (vs. a bare "no options" the user can't explain).
  const hasQualifiedCandidate = React.useMemo(
    () => listHasQualifiedCandidate(allUsers, taskTypeKey),
    [allUsers, taskTypeKey],
  );

  const handlePickerChange = useCallback(
    (_event: React.SyntheticEvent, newValues: AssignableUser[]) => {
      setSelected(newValues.map((user) => ({ user })));
    },
    [],
  );

  const handleRemove = useCallback((userKey: string) => {
    setSelected((prev) => prev.filter((e) => e.user.AppUserKey !== userKey));
  }, []);

  /**
   * BUG-2026-0093 — remove an ALREADY-PERSISTED assignee from the task.
   * Soft-deletes every per-week EmployeeAssignment row for that user, refreshes
   * the dialog's "Currently assigned" list in place, and tells the parent to
   * refresh the row's chips. The dialog stays open so several people can be
   * removed in a row.
   */
  const handleUnassignExisting = useCallback(
    (userKey: string) => {
      if (!task || removingExistingKey) return;
      const displayName =
        existingAssignees.find((a) => a.UserKey === userKey)?.displayName ?? 'This person';
      // Skill-gate #4 — warn first if this removes the last supervisor; the
      // removal runs immediately when safe, or on confirm when it would strand.
      void guardRemoval(task.ScheduleTaskKey, userKey, displayName, scenarioKey, async () => {
        setRemovingExistingKey(userKey);
        setError(null);
        try {
          await unassignUserFromTask(existingAssignees, userKey);
          const rows = await listTaskAssignees(task.ScheduleTaskKey, scenarioKey);
          setExistingAssignees(rows);
          onAssigneesChanged?.();
        } catch (err) {
          const axiosBody = (err as { response?: { data?: { error?: string } } })
            ?.response?.data?.error;
          const fallback = err instanceof Error ? err.message : 'Failed to remove assignee.';
          setError(axiosBody || fallback);
        } finally {
          setRemovingExistingKey(null);
        }
      });
    },
    [task, existingAssignees, removingExistingKey, onAssigneesChanged, scenarioKey, guardRemoval],
  );

  /**
   * Internal helper that calls bulkAssignFromTask. Every row writes percent
   * 100 (presence — the server pins it anyway). Handles SkillGateError; an
   * AbsenceDayConflictError (Q3B hard block) surfaces its server message
   * VERBATIM in the error alert — no Save-anyway exists for it.
   */
  const doSave = async (confirmSkillGate = false) => {
    if (!task || selected.length === 0) return;
    setSaving(true);
    setError(null);
    try {
      const result = await bulkAssignFromTask({
        scheduleTaskKey: task.ScheduleTaskKey,
        userPercents: selected.map((e) => ({
          userKey: e.user.AppUserKey,
          percent: 100,
        })),
        // W2-8 (warn-only): "Assign anyway" — skip the advisory mentor gate.
        ...(confirmSkillGate ? { confirmSkillGate: true } : {}),
        // Per-machine staffing (079) — bind to the machine the picker staffed.
        ...(machineBookingKey != null ? { machineBookingKey } : {}),
        // FEAT-2026-0007 6C.1 — thread scenarioKey for scenario-scoped saves.
        ...(scenarioKey !== undefined ? { scenarioKey } : {}),
      });
      // G2-7: pass the non-blocking overload advisory along with the count.
      onSaved(result?.created?.length ?? 0, weekOverloadHint(result?.weekOverloaded));
      onClose();
    } catch (err) {
      if (err instanceof SkillGateError) {
        setSkillGateBlocked(err.blockedUsers);
        setSkillGateTaskTypeKey(err.taskTypeKey);
        setSkillGateOpen(true);
      } else {
        // AbsenceDayConflictError.message IS the server message (verbatim).
        // Other axios errors put the backend's helpful message in
        // err.response.data.error — surface it; fall back to err.message.
        const axiosBody = (err as { response?: { data?: { error?: string } } })?.response?.data?.error;
        const fallback = err instanceof Error ? err.message : 'Failed to save assignments.';
        setError(axiosBody || fallback);
      }
    } finally {
      setSaving(false);
    }
  };

  const handleSave = async () => {
    await doSave(false);
  };

  /**
   * W2-8 (warn-only): "Assign anyway" in the Mentor Required dialog — proceed
   * WITHOUT a mentor (the gate is advisory).
   */
  const handleAssignAnyway = async () => {
    setSkillGateOpen(false);
    await doSave(true);
  };

  /**
   * Called by MentorRequiredModal when the user picks a mentor.
   * Adds the mentor to the selected list (presence like everyone — the 25%
   * oversight convention died with the percent model) if not already included.
   * The user then clicks Assign again.
   */
  const handleAddMentor = useCallback((mentor: EligibleMentor) => {
    setSkillGateOpen(false);

    setSelected((prev) => {
      // Don't add if already in the list
      if (prev.some((e) => e.user.AppUserKey === mentor.AppUserKey)) return prev;

      // Find the matching AssignableUser from the loaded list (or synthesise one)
      const existing = allUsers.find((u) => u.AppUserKey === mentor.AppUserKey);
      const syntheticUser: AssignableUser = existing ?? {
        AppUserKey: mentor.AppUserKey,
        DisplayName: mentor.DisplayName,
        Email: mentor.Email,
        Tier: null,
        WorkGroup: null,
        JobClass: null,
      };

      return [...prev, { user: syntheticUser }];
    });
  }, [allUsers]);

  const pickerValue = selected.map((e) => e.user);

  return (
    <Dialog
      open={open}
      onClose={saving ? undefined : onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: `${theme.border.radius}px`,
          border: `1px solid ${theme.border.color}`,
        },
      }}
    >
      {/* Title */}
      <DialogTitle
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          pb: 1,
          borderBottom: `1px solid ${theme.border.color}`,
        }}
      >
        <PersonAdd sx={{ color: theme.schedule.employeeAlloc, fontSize: 22 }} />
        <Box sx={{ flex: 1 }}>
          <Typography variant="subtitle1" fontWeight={700} lineHeight={1.2}>
            Assign Employees
          </Typography>
          {task && (
            <Typography variant="caption" color="text.secondary">
              {task.Name} &nbsp;·&nbsp; {fmtDate(task.StartDate)} – {fmtDate(task.EndDate)}
            </Typography>
          )}
          {/* RM-5 + lab answer Q2 (2026-07-10): continuous staffing model in one
              line — availability + save both judge every working day of the run. */}
          {task && !task.IsPrepStep && (task.RunMode ?? 'attended') === 'continuous' && (
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
              Continuous (24/7) test — staffed every working day of the run.
            </Typography>
          )}
        </Box>
        <IconButton size="small" onClick={onClose} disabled={saving}>
          <Close fontSize="small" />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ pt: 2, pb: 1 }}>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {/* Currently assigned — each chip is removable (BUG-2026-0093) */}
        {uniqueExisting.length > 0 && (
          <Box sx={{ mb: 2 }}>
            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, display: 'block', mb: 0.5 }}>
              Currently assigned ({uniqueExisting.length})
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
              {uniqueExisting.map((a) => {
                const chipColor = a.Status === 'Committed'
                  ? theme.schedule.committed
                  : theme.schedule.employeeAlloc;
                const isRemoving = removingExistingKey === a.UserKey;
                return (
                  <Tooltip
                    key={a.UserKey}
                    title={isRemoving ? 'Removing…' : `Remove ${a.displayName} from this task`}
                    arrow
                  >
                    <Chip
                      label={`${a.displayName}${a.Status === 'Committed' ? ' ·  committed' : ''}`}
                      size="small"
                      onDelete={removingExistingKey ? undefined : () => void handleUnassignExisting(a.UserKey)}
                      deleteIcon={
                        isRemoving
                          ? <CircularProgress size={13} sx={{ color: chipColor }} />
                          : <Close aria-label={`Remove ${a.displayName}`} />
                      }
                      sx={{
                        fontSize: '0.72rem',
                        backgroundColor: alpha(chipColor, a.Status === 'Committed' ? 0.16 : 0.12),
                        color: chipColor,
                        fontWeight: 600,
                        '& .MuiChip-deleteIcon': {
                          color: alpha(chipColor, 0.7),
                          fontSize: 16,
                          '&:hover': { color: theme.schedule.conflictHard },
                        },
                      }}
                    />
                  </Tooltip>
                );
              })}
            </Box>
          </Box>
        )}

        {/*
          Employee picker — excludes users already assigned to this task AND
          (BUG-2026-0094) users with no SkillMatrix score for this task type.
        */}
        <Autocomplete
          multiple
          loading={loadingUsers}
          options={pickableUsers}
          value={pickerValue}
          getOptionLabel={(u) => u.DisplayName}
          isOptionEqualToValue={(a, b) => a.AppUserKey === b.AppUserKey}
          // IMP2-2 — a dated absence on the booking's days makes the save a
          // guaranteed hard 409 (Q3B, no bypass), so the option is disabled;
          // its row still shows the "unavailable (day off)" reason. Never
          // disabled while availability is unknown (save-time gate rules).
          getOptionDisabled={(u) =>
            isPickBlockedByAbsence(capacity.get(u.AppUserKey), !availabilityUnknown)
          }
          onChange={handlePickerChange}
          noOptionsText={
            loadingUsers
              ? 'Loading…'
              : // BUG-2026-0094 — list loaded but nobody is qualified for this
                // task type (all candidates lacked a SkillMatrix score).
                !hasQualifiedCandidate
                ? 'No qualified users for this task type'
                : 'No more users to assign'
          }
          renderInput={(params) => (
            <TextField
              {...params}
              label="Select employees"
              placeholder="Search by name…"
              size="small"
              InputProps={{
                ...params.InputProps,
                endAdornment: (
                  <>
                    {loadingUsers && <CircularProgress size={16} />}
                    {params.InputProps.endAdornment}
                  </>
                ),
              }}
            />
          )}
          renderOption={(props, option) => (
            <Box component="li" {...props} sx={{ py: 0.6 }}>
              <AssigneeOptionRow
                displayName={option.DisplayName}
                tier={option.Tier}
                skillLevel={option.SkillLevel}
                showSkill={Boolean(taskTypeKey)}
                load={capacity.get(option.AppUserKey) ?? null}
                showLoad={Boolean(relevantWeek)}
                loadUnknown={availabilityUnknown}
              />
            </Box>
          )}
          componentsProps={{ paper: { sx: { minWidth: 360 } } }}
          renderTags={() => null}  // Tags rendered in the table below instead
          sx={{ mb: selected.length > 0 ? 2 : 0 }}
        />

        {/* Per-employee rows */}
        {selected.length > 0 && (
          <Box
            sx={{
              border: `1px solid ${theme.border.color}`,
              borderRadius: `${theme.border.radius}px`,
              overflow: 'hidden',
            }}
          >
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell
                    sx={{
                      backgroundColor: theme.table.headerBg,
                      fontWeight: 700,
                      fontSize: '0.72rem',
                      py: 0.75,
                    }}
                  >
                    <Tooltip
                      title="Each person is busy (a full day) on every working day this task covers."
                      arrow
                    >
                      <span>Employee</span>
                    </Tooltip>
                  </TableCell>
                  <TableCell
                    sx={{
                      backgroundColor: theme.table.headerBg,
                      width: 40,
                    }}
                  />
                </TableRow>
              </TableHead>
              <TableBody>
                {selected.map((entry) => (
                  <TableRow key={entry.user.AppUserKey} hover>
                    <TableCell sx={{ py: 0.75, px: 1.5 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                        <Typography variant="body2" sx={{ fontSize: '0.82rem' }}>
                          {entry.user.DisplayName}
                        </Typography>
                        <TierChip tier={entry.user.Tier} />
                      </Box>
                    </TableCell>
                    <TableCell sx={{ py: 0.5, px: 0.5 }}>
                      <IconButton
                        size="small"
                        onClick={() => handleRemove(entry.user.AppUserKey)}
                        sx={{
                          color: theme.schedule.conflictHard,
                          '&:hover': {
                            backgroundColor: alpha(theme.schedule.conflictHard, 0.08),
                          },
                        }}
                      >
                        <Close fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Box>
        )}

        {selected.length === 0 && !loadingUsers && (
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ textAlign: 'center', py: 2, fontSize: '0.82rem' }}
          >
            Search for employees above to add them to this task.
          </Typography>
        )}
      </DialogContent>

      <DialogActions
        sx={{
          px: 2.5,
          py: 1.5,
          borderTop: `1px solid ${theme.border.color}`,
          gap: 1,
        }}
      >
        <Button onClick={onClose} disabled={saving} size="small">
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={saving || selected.length === 0}
          size="small"
          startIcon={saving ? <CircularProgress size={14} /> : <PersonAdd />}
          sx={{
            backgroundColor: theme.schedule.employeeAlloc,
            '&:hover': { backgroundColor: alpha(theme.schedule.employeeAlloc, 0.85) },
          }}
        >
          {saving ? 'Saving…' : `Assign ${selected.length > 0 ? `(${selected.length})` : ''}`}
        </Button>
      </DialogActions>

      {/* Phase 3 Item 7 — Mentor-required modal (advisory since W2-8) */}
      <MentorRequiredModal
        open={skillGateOpen}
        taskTypeKey={skillGateTaskTypeKey}
        blockedUsers={skillGateBlocked}
        onAddMentor={handleAddMentor}
        onAssignAnyway={() => { void handleAssignAnyway(); }}
        onClose={() => setSkillGateOpen(false)}
      />

      {/* Skill-gate #4 — warn-and-confirm when removing the last supervisor. */}
      <ConfirmDialog state={removalConfirm} onClose={closeRemovalConfirm} />
    </Dialog>
  );
};
